import * as TabsPrimitive from "@radix-ui/react-tabs";
import * as React from "react";
import { cn } from "../utils";

export type TabsProps = TabsPrimitive.TabsProps;

export const Tabs = TabsPrimitive.Root;

export interface TabsListProps extends TabsPrimitive.TabsListProps {}
export const TabsList = React.forwardRef<HTMLDivElement, TabsListProps>(
  ({ className, ...props }, ref) => (
    <TabsPrimitive.List
      ref={ref}
      className={cn(
        "inline-flex items-center gap-1 rounded-lg p-1 text-sm font-medium",
        "bg-[var(--color-surface,#fff)] bg-opacity-80 shadow-sm",
        "border border-neutral-200/70 dark:border-white/10",
        className
      )}
      {...props}
    />
  )
);
TabsList.displayName = TabsPrimitive.List.displayName;

export interface TabsTriggerProps extends TabsPrimitive.TabsTriggerProps {}
export const TabsTrigger = React.forwardRef<HTMLButtonElement, TabsTriggerProps>(
  ({ className, ...props }, ref) => (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        // The active tab is a filled pill: the previous white-on-white
        // treatment left it near-indistinguishable from its own track.
        "relative inline-flex min-w-[96px] items-center justify-center gap-2 rounded-md px-3 py-2 transition-colors",
        // No dark: variants here. These sites render light-only, but the preset
        // resolves dark: from prefers-color-scheme, so such a variant would fire
        // on a white page for any viewer whose OS is in dark mode.
        "font-medium text-neutral-600",
        "hover:bg-primary/10 hover:text-neutral-900",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2",
        // Dark-on-primary matches the site's primary button (7.4:1); white text
        // on this light blue is only 2.5:1 and fails WCAG AA.
        "data-[state=active]:bg-primary data-[state=active]:text-foreground data-[state=active]:shadow-sm",
        "data-[state=active]:hover:bg-primary data-[state=active]:hover:text-foreground",
        className
      )}
      {...props}
    />
  )
);
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

export interface TabsContentProps extends TabsPrimitive.TabsContentProps {}
export const TabsContent = React.forwardRef<HTMLDivElement, TabsContentProps>(
  ({ className, ...props }, ref) => (
    <TabsPrimitive.Content
      ref={ref}
      className={cn(
        "rounded-lg border border-neutral-200 dark:border-white/10",
        "bg-[var(--color-surface,#fff)] p-4 shadow-sm",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2",
        className
      )}
      {...props}
    />
  )
);
TabsContent.displayName = TabsPrimitive.Content.displayName;
